// File: src/pages/host/HostLogin.jsx
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function HostLogin() {
  const navigate = useNavigate();
  const api = process.env.REACT_APP_API_URL;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const canSubmit = useMemo(() => {
    return String(email).trim().length > 0 && String(password).length > 0 && !loading;
  }, [email, password, loading]);

  async function tryLogin(endpoint) {
    const res = await axios.post(`${api}${endpoint}`, {
      email: String(email).trim().toLowerCase(),
      password: String(password),
    });

    // Try common response shapes
    const token =
      res.data?.accessToken ||
      res.data?.token ||
      res.data?.authToken ||
      res.data?.data?.accessToken ||
      res.data?.data?.token ||
      '';

    const user =
      res.data?.user ||
      res.data?.data?.user ||
      res.data?.currentUser ||
      null;

    const userId =
      user?._id ||
      res.data?.userId ||
      res.data?.id ||
      '';

    return { token, user, userId };
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setErr('');
    setLoading(true);

    try {
      // Try a couple common auth endpoints (keep both; whichever exists will work)
      let result = null;

      try {
        result = await tryLogin('/auth/login');
      } catch {
        result = await tryLogin('/users/login');
      }

      const { token, user, userId } = result || {};

      if (!token) {
        throw new Error('Login failed (missing token).');
      }

      // Optional: host-only guard if your backend returns isHost
      if (user && user.isHost === false) {
        throw new Error('This account is not a host.');
      }

      // Persist in the formats your app already uses
      localStorage.setItem('accessToken', token);
      localStorage.setItem('token', token);
      localStorage.setItem('authToken', token);

      if (userId) localStorage.setItem('userId', String(userId));

window.location.href = '/host';
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2?.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.kicker}>Host</div>
        <div style={S.title}>Log in</div>
        <div style={S.sub}>Use your host account to access the host dashboard.</div>

        <form onSubmit={onSubmit} style={{ marginTop: 14 }}>
          <div style={S.label}>Email</div>
          <input
            style={S.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            autoComplete="email"
          />

          <div style={{ ...S.label, marginTop: 10 }}>Password</div>
          <input
            style={S.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            type="password"
            autoComplete="current-password"
          />

          {err ? <div style={S.err}>{err}</div> : null}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{ ...S.btn, ...(canSubmit ? S.btnDark : S.btnDisabled) }}
          >
            {loading ? 'Logging in…' : 'Log in'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            style={S.linkBtn}
          >
            Back to landing
          </button>
        </form>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: '#0b0b0b',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
    border: '1px solid #eef0f2',
    padding: 16,
  },
  kicker: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6b7280',
  },
  title: {
    marginTop: 6,
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: '-0.01em',
    color: '#0b0b0b',
  },
  sub: {
    marginTop: 6,
    fontSize: 13.5,
    color: '#4b5563',
    lineHeight: 1.45,
  },
  label: {
    fontSize: 12,
    fontWeight: 900,
    color: '#374151',
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  input: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    padding: '0 12px',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  err: {
    marginTop: 10,
    fontSize: 12,
    color: '#b91c1c',
    fontWeight: 800,
  },
  btn: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.04,
    cursor: 'pointer',
    marginTop: 12,
  },
  btnDark: { background: '#0b0b0b', color: '#fff', border: '1px solid #0b0b0b' },
  btnDisabled: { background: '#e5e7eb', color: '#6b7280', cursor: 'not-allowed' },
  linkBtn: {
    width: '100%',
    marginTop: 10,
    background: 'transparent',
    border: 'none',
    color: '#111827',
    fontWeight: 800,
    cursor: 'pointer',
  },
};
